import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ContactSection from '../components/ContactSection';
import { Camera, CheckCircle2, Eye, Smartphone, Ruler, Building2 } from 'lucide-react';
import { breadcrumbSchema } from '../utils/structuredData';

const MatterportVirtualToursPage = () => {
  const features = [
    'Photorealistic 3D virtual walkthroughs',
    'Accurate floor plans and measurements',
    'Dollhouse and floor plan views',
    'Embeddable on any website',
    'Guided virtual tour creation',
    'Integration with Google Street View'
  ];

  const benefits = [
    {
      icon: Eye,
      title: 'Immersive Experience',
      description: 'Allow viewers to explore spaces as if physically present, increasing engagement and reducing unnecessary site visits.'
    },
    {
      icon: Smartphone,
      title: 'Access Anywhere',
      description: 'Share tours on any device — desktop, tablet or mobile — with no app download required for viewers.'
    },
    {
      icon: Ruler,
      title: 'Built-in Measurements',
      description: 'Measure any distance within the 3D model directly in the browser, accurate to within 1% under 3 metres.'
    }
  ];

  const useCases = [
    {
      title: 'Property Marketing',
      description: 'Estate agents and developers use Matterport virtual tours to showcase properties online 24/7. Listings with 3D tours receive significantly more engagement and qualified enquiries than those without.'
    },
    {
      title: 'Construction Documentation',
      description: 'Capture construction progress at each stage with time-stamped 3D records. Project managers, architects and clients can review site conditions remotely without the cost and time of physical visits.'
    },
    {
      title: 'Facilities Management',
      description: 'Create a permanent digital twin of your building for ongoing maintenance planning, space management and compliance documentation. Tag assets, add notes and link documents directly within the model.'
    }
  ];

  const faqs = [
    {
      question: 'How long does a Matterport scan take?',
      answer: 'A typical property scan takes between 1 and 4 hours depending on the size and complexity of the space. A standard 2-bedroom flat can be completed in around 1.5 hours, while a large commercial building may require a full day.'
    },
    {
      question: 'What equipment do you use for Matterport scanning?',
      answer: 'We use the latest Matterport Pro3 and Pro2 cameras, which capture high-resolution 3D data and HDR photography simultaneously. For larger spaces, we also deploy Leica laser scanners integrated with the Matterport platform.'
    },
    {
      question: 'How much does a Matterport virtual tour cost in London?',
      answer: 'Our Matterport scanning services start from £295 for smaller spaces. Pricing depends on the size of the property, the number of scan positions required, and any additional deliverables such as floor plans or measurements. Contact us for a tailored quote.'
    },
    {
      question: 'How is the virtual tour delivered?',
      answer: 'Your completed 3D tour is hosted on the Matterport platform and accessible via a unique URL. You can embed it on your website, share it on social media, or send it directly to clients. We also offer self-hosted solutions for clients who require it.'
    },
    {
      question: 'What is the difference between Matterport and laser scanning?',
      answer: 'Matterport cameras capture 3D data optimised for visual walkthroughs and virtual tours, while laser scanners capture survey-grade point cloud data for measurement and modelling. Matterport is ideal for property marketing and remote viewing, while laser scanning is used for as-built surveys and BIM modelling. We offer both services and can advise which is best for your project.'
    },
    {
      question: 'Can I use a Matterport tour for facilities management?',
      answer: 'Yes. Matterport tours serve as an excellent digital twin for facilities management. You can tag assets, embed maintenance documents, link to building management systems, and create a living digital record of your property. Our clients in healthcare, education and corporate real estate use Matterport tours as the visual layer of their FM workflows.'
    }
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Matterport Virtual Tours",
    "name": "Matterport Virtual Tours London",
    "provider": {
      "@type": "Organization",
      "name": "Digital Scanning Group",
      "url": "https://www.digitalscanninggroup.com"
    },
    "areaServed": [
      { "@type": "Place", "name": "London" },
      { "@type": "Place", "name": "United Kingdom" }
    ],
    "description": "Professional Matterport 3D virtual tour scanning services in London for property marketing, construction documentation and facilities management.",
    "offers": {
      "@type": "Offer",
      "priceCurrency": "GBP",
      "price": "295",
      "priceValidUntil": "2027-12-31",
      "availability": "https://schema.org/InStock"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Matterport Virtual Tours London | 3D Virtual Tour Scanning Service"
        description="Professional Matterport virtual tours in London. Immersive 3D scanning for property marketing, construction and facilities management. From £295."
        keywords="matterport virtual tours london, 3D virtual tour london, matterport scanning service, matterport pro3, virtual tour london, 3D property tour"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/matterport-virtual-tours-london` : ''}
        structuredData={[
          serviceSchema,
          faqSchema,
          breadcrumbSchema([
            { name: 'Home', url: typeof window !== 'undefined' ? window.location.origin : '' },
            { name: 'Matterport Virtual Tours London', url: typeof window !== 'undefined' ? `${window.location.origin}/matterport-virtual-tours-london` : '' }
          ])
        ]}
      />
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-50 rounded-full mb-6">
              <Camera className="h-6 w-6 text-blue-600" />
              <span className="text-blue-600 font-semibold">MATTERPORT SCANNING</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Matterport Virtual Tours London
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Capture any space in photorealistic 3D with our professional Matterport scanning service. We create immersive virtual tours that let your audience explore properties, buildings and venues from anywhere in the world.
            </p>
            <div className="mt-8">
              <Link
                to="/pricing"
                className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                Get a Quote
              </Link>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-16">
            <img
              src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxwcm9wZXJ0eSUyMHZpcnR1YWwlMjB0b3VyfGVufDB8fHx8&ixlib=rb-4.1.0&q=85&w=1400"
              alt="Matterport Virtual Tour of a London Property"
              className="w-full h-[500px] object-cover"
            />
          </div>

          {/* What is Matterport */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                What Is a Matterport Virtual Tour?
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  A Matterport virtual tour is a fully immersive, interactive 3D model of a physical space. Unlike traditional photography or video, a Matterport scan allows viewers to navigate through a property at their own pace, exploring every room and detail as if they were physically walking through it. The technology captures both the visual appearance and the precise spatial dimensions of any interior or exterior environment.
                </p>
                <p>
                  At Digital Scanning Group, we use the latest Matterport Pro3 and Pro2 cameras to capture spaces across London and the wider UK. Each scan position captures millions of data points and high-dynamic-range photography, which our processing pipeline stitches together into a seamless, navigable 3D model. The result is a digital twin that can be viewed in a web browser on any device — no specialist software or downloads required.
                </p>
                <p>
                  Matterport virtual tours are used across a wide range of industries. Property agents use them to market listings and reduce unnecessary viewings. Construction teams use them to document site conditions at every stage of a project. Facilities managers use them to maintain a living digital record of their buildings — creating what is known as a <Link to="/blog/what-is-a-digital-twin" className="text-blue-600 hover:underline">digital twin</Link> of their property. Whatever the application, the underlying benefit is the same: accurate, accessible spatial information that saves time and money.
                </p>
                <p>
                  If you are wondering about the investment involved, our guide on <Link to="/blog/how-much-does-matterport-scan-cost-uk" className="text-blue-600 hover:underline">how much a Matterport scan costs in the UK</Link> breaks down pricing factors in detail. For architects considering the technology, we have also written about <Link to="/blog/matterport-for-architects" className="text-blue-600 hover:underline">how Matterport supports architectural workflows</Link>. And if you are unsure whether you need a Matterport tour or a full laser scan, our comparison of <Link to="/blog/matterport-vs-laser-scanning-which-do-you-need" className="text-blue-600 hover:underline">Matterport vs laser scanning</Link> explains the key differences.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What You Get</h3>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/pricing"
                className="mt-8 w-full inline-block text-center px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                Get a Quote
              </Link>
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">How the Scanning Process Works</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              From initial contact to delivered virtual tour, our streamlined process ensures quality results with minimal disruption.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: '01', title: 'Site Assessment', desc: 'We discuss your requirements and plan the scan to cover every area you need captured.' },
                { step: '02', title: 'On-Site Scanning', desc: 'Our technician arrives with Matterport Pro3 equipment and systematically scans the space.' },
                { step: '03', title: 'Processing', desc: 'Raw scan data is uploaded and processed into a complete, navigable 3D model within 24-48 hours.' },
                { step: '04', title: 'Delivery', desc: 'You receive your hosted tour link, embed code, and any additional deliverables such as floor plans.' }
              ].map((item, index) => (
                <div key={index} className="p-6 bg-blue-50 rounded-2xl text-center">
                  <div className="text-4xl font-bold text-blue-600 mb-3">{item.step}</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Benefits of Matterport Virtual Tours</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="p-8 bg-blue-50 rounded-2xl text-center">
                    <div className="inline-flex p-4 bg-blue-100 rounded-xl mb-4">
                      <Icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Use Cases */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Who Uses Matterport Virtual Tours?</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              From property marketing to construction documentation, Matterport technology serves a wide range of industries and applications.
            </p>
            <div className="space-y-6">
              {useCases.map((useCase, index) => (
                <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{useCase.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Why Choose DSG */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Why Choose Digital Scanning Group</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              As an official Matterport Service Provider, we combine technical expertise with a deep understanding of how 3D data is used in practice.
            </p>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  'Official Matterport Service Provider with Pro3 and Pro2 equipment',
                  'Over 500 completed scanning projects across London and the UK',
                  'Fast turnaround — most tours delivered within 48 hours',
                  'Additional deliverables including floor plans, point clouds and BIM models',
                  'Dedicated project manager for every job',
                  'Competitive pricing starting from £295'
                ].map((point, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Related Services */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Related Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link to="/point-cloud-surveys-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Building2 className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Point Cloud Surveys</h3>
                <p className="text-gray-600 text-sm">High-accuracy laser scanning for as-built surveys and renovation projects.</p>
              </Link>
              <Link to="/scan-to-bim-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Building2 className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Scan to BIM</h3>
                <p className="text-gray-600 text-sm">Convert scan data into accurate Revit BIM models for design and construction.</p>
              </Link>
              <Link to="/commercial-property-3d-tours-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Building2 className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Commercial Property Tours</h3>
                <p className="text-gray-600 text-sm">3D virtual tours for offices, retail and industrial properties.</p>
              </Link>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Create Your Matterport Virtual Tour?</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Get in touch today for a free consultation and quote. We cover all of London and the wider UK.
            </p>
            <Link
              to="/pricing"
              className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default MatterportVirtualToursPage;
